import React, { useState, useRef } from 'react';
import { UploadCloud, ArrowRight, Check, AlertCircle } from 'lucide-react';
import ClosingCTA from '../components/ClosingCTA';
import SEO from '../components/SEO';
import { track } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Careers() {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const formRef = useRef(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    let resumeUrl = '';
    let resumeName = fileName || '';

    try {
      // 1. Upload resume first (if provided)
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await fetch(`${API}/api/careers/upload`, { method: 'POST', body: fd });
        if (!up.ok) {
          const j = await up.json().catch(() => ({}));
          throw new Error(j.detail || `Upload failed (${up.status})`);
        }
        const upJson = await up.json();
        resumeUrl = upJson.url || '';
        resumeName = upJson.filename || resumeName;
      }

      // 2. Submit the enquiry with the resume URL attached
      const f = new FormData(formRef.current);
      const payload = {
        name: (f.get('name') || '').toString().trim(),
        email: (f.get('email') || '').toString().trim(),
        location: (f.get('location') || '').toString().trim(),
        message: (f.get('message') || '').toString().trim(),
        resume_filename: resumeName,
        resume_url: resumeUrl,
      };
      const res = await fetch(`${API}/api/careers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
      track('Careers Applied', { location: payload.location || '(unknown)', has_resume: !!payload.resume_url });
    } catch (err) {
      setError(err?.message?.startsWith('Only PDF') || err?.message?.startsWith('Resume too large')
        ? err.message
        : 'We could not send that just now. Please retry, or email careers@digitalworks.co directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Careers — the engineering job is changing"
        description="Join DigitalWorks. Review test plans before code exists, own requirements across several agent-run workstreams, and design the guardrails that let any of it near production. Send us your work."
        canonical="https://digitalworks.co/careers"
      />
      <section className="pt-16 pb-10">
        <div className="dwx">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Careers</div>
          <h1 className="serif-tight text-[48px] md:text-[80px] leading-[0.96] mt-4 max-w-4xl">The engineering job is changing. We would rather build the new one.</h1>
          <div className="mt-12 max-w-3xl text-[17px] leading-relaxed text-[#1A1A1A]/85 space-y-4">
            <p>Here you review test plans before code exists, own requirements across several agent-run workstreams at once, and design the guardrails that let any of it near production.</p>
            <p>It is closer to systems design and product ownership than to typing implementations. Some engineers find that a loss. Others have been waiting for it.</p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="dwx max-w-3xl">
          <div className="mono text-[11px] tracking-[0.22em] uppercase text-[#1A1A1A]/60">Join us</div>
          <h2 className="serif-tight text-[38px] md:text-[52px] leading-[0.98] mt-3">Send us your work.</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[#1A1A1A]/75">A resume, and something you have shipped. Anything you would want another engineer to look at. If a role that fits your background is open, we&rsquo;ll come back with the specifics.</p>

          {submitted ? (
            <div className="mt-10 rounded-[20px] bg-[#22A94F]/12 border border-[#22A94F]/30 p-8" data-testid="careers-submitted" role="status" aria-live="polite">
              <Check size={28} className="text-[#178838]" />
              <div className="serif text-[24px] mt-4">We have it.</div>
              <p className="mt-2 text-[14px] text-[#1A1A1A]/75">You will hear from a person within one working day.</p>
            </div>
          ) : (
            <form ref={formRef} onSubmit={handleSubmit} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4">
              <input required name="name" placeholder="Name" data-testid="careers-name" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
              <input required name="email" type="email" placeholder="Work email" data-testid="careers-email" className="px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
              <input name="location" placeholder="Where you are based (city, country)" data-testid="careers-location" className="md:col-span-2 px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px]" />
              <textarea name="message" placeholder="Tell us about something you shipped." data-testid="careers-message" className="md:col-span-2 px-4 py-3 rounded-lg bg-[#FBFAF6] border border-[#1A1A1A]/15 focus:outline-none focus:border-[#22A94F] text-[14px] min-h-[140px]" />
              <label className="md:col-span-2 cursor-pointer flex items-center gap-3 px-4 py-3 rounded-lg bg-[#FBFAF6] border border-dashed border-[#1A1A1A]/25 text-[14px] text-[#1A1A1A]/70">
                <UploadCloud size={18} />
                <span>{fileName || 'Upload resume (PDF, DOC or DOCX — max 10 MB)'}</span>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  data-testid="careers-resume"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setFile(f || null);
                    setFileName(f?.name || '');
                  }}
                />
              </label>
              {error && (
                <div className="md:col-span-2 flex items-start gap-2 text-[13px] text-[#B4351A]" role="alert" data-testid="careers-error">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              <button type="submit" disabled={submitting} data-testid="careers-submit" className="md:col-span-2 justify-self-start inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1A1A1A] text-[#FBFAF6] text-[14px] font-medium hover:bg-[#2B2B2B] disabled:opacity-60 disabled:cursor-not-allowed">
                {submitting ? 'Sending…' : (<>Send <ArrowRight size={16} /></>)}
              </button>
            </form>
          )}
        </div>
      </section>

      <ClosingCTA />
    </div>
  );
}
